<template>
  <v-container class="flow-measurement">
    <!-- Flow Measurement Method Dialog -->
    <v-dialog
      v-if="flowMeasurementMethodDialog"
      v-model="flowMeasurementMethodDialog"
      content-class="flow-measurement-method-dialog"
    >
      <v-card>
        <v-row>
          <v-col cols="8" offset="2" class="px-0">
            <v-card-title tag="p" class="flow-measurement-method-dialog-title text-break justify-center mb-2 text-center px-0">
              Método de medición de caudal
            </v-card-title>
          </v-col>
          <v-col cols="2" class="px-2 text-end">
            <v-btn icon @click="flowMeasurementMethodDialog = false">
              <v-icon color="#00202F">close</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-card-text>
          <p>
            Para realizar la medición de caudal, se utilizó un caudalímetro ultrasónico. Este dispositivo mide el flujo de líquidos o gases
            en tuberías o conductos mediante ondas ultrasónicas, determinando así la velocidad del fluido y calculando el caudal
            volumétrico.
            <br />
            <br />
            Una de sus principales ventajas es su portabilidad y facilidad de instalación, lo que permite operar sin alterar los
            componentes del sistema a medir. Esto garantiza una medición confiable desde el momento de su instalación.
          </p>
          <v-img :src="require('../../assets/audits/flowMeasurementMethod.png')" max-width="315px" class="mt-5"/>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Comments Dialog -->
    <v-dialog
      v-if="commentsDialog"
      v-model="commentsDialog"
      content-class="comments-dialog"
    >
      <v-card>
        <v-card-text class="py-3">
            {{ comments }}
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Images Dialog -->
    <v-dialog
      v-if="imagesDialog"
      v-model="imagesDialog"
      content-class="comments-dialog"
    >
      <v-card color="black">
        <v-carousel
          v-model="carouselModel"
          :continuous="false"
          class="flow-measurement-carousel"
          hide-delimiters
          height="400px"
          ref="flow-measurement-carousel"
          style="align-content: center; align-items: center; justify-content: center;"
        >
          <v-carousel-item
            v-for="(image, imageIndex) in images"
            :key="imageIndex"
            style="align-content: center; align-items: center; justify-content: center;"
          >
          <v-img :src="image.file.url" max-width="100%" max-height="100%" contain class="d-flex align-center justify-center" />
            <div class="gradient-layer">
              <p class="gradient-layer-text">{{image.label || ''}}</p>
            </div>
          </v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>

    <v-row class="mt-3">
      <v-col>
        <p class="flow-measurement-title mb-0">Detalle de medición de caudal</p>
        <a class="flow-measurement-subtitle mb-0" @click="flowMeasurementMethodDialog = true">Método de medición</a>
      </v-col>
      <v-col align="end" style="align-content: center;">
        <v-icon class="d-inline icon-center" color="#00202F" size="1rem">date_range</v-icon>
        <p class="mb-0 d-inline">{{parseDate(flowMeasurementData.workDate)}}</p>
      </v-col>
    </v-row>
    <v-row>
      <v-container fluid class="Content d-flex pa-0">
        <div class="Options overflow-hidden pr-1 mr-3">
          <v-radio-group v-model="selectedOption" mandatory class="px-4 pt-0">
            <v-radio
              v-for="option in options"
              :key="option"
              :label="option"
              :value="option"
              color="#0098E1"
              :style="{'font-weight': selectedOption === option ? '600' : '400'}"
            />
          </v-radio-group>
        </div>
        <div class="Graphic fill-height align-center justify-left">
          <template v-if="selectedOption === 'Recomendaciones'">
            <div style="height: 100%; place-content: center;" v-if="checkObservationsDocumentEmpty()" >
              <p class="text-center mb-0">No hay recomendaciones</p>
            </div>
            <KeystoneDocumentRenderer v-else :document="flowMeasurementData.observations.document" />
          </template>
          <template v-if="selectedOption === 'Resultados'">
            <div v-if="flowMeasurementData.flowSamples.length === 0" style="height: 100%; place-content: center;">
              <p class="text-center mb-0">No hay mediciones</p>
            </div>
            <template v-else>
              <v-row class="mt-3" v-for="(sample, sampleIndex) in sortedFlowSamples" :key="sampleIndex">
                <v-col>
                  <v-row>
                    <p class="mb-0 ml-2" style="font-size: 1.13rem; font-weight: 600;">Medición {{ sampleIndex + 1 }}</p>
                    <v-icon v-if="getFlowSamplesImages([sample], sampleIndex).length > 0" @click="openImagesDialog(getFlowSamplesImages([sample], sampleIndex))" class="ml-4" size="1.38rem" color="#0099E1">mdi-camera-outline</v-icon>
                  </v-row>
                  <v-row>
                    <v-col cols="3" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Hora de medición</p>
                    </v-col>
                    <v-col cols="3" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Caudal medido (m3/h)</p>
                    </v-col>
                    <v-col cols="1" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Q</p>
                    </v-col>
                    <v-col cols="1" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 400;">S</p>
                    </v-col>
                    <v-col cols="1" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Vel (m/s)</p>
                    </v-col>
                    <v-col cols="3" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Presión de trabajo (PSI)</p>
                    </v-col>
                  </v-row>
                  <v-row class="mt-1">
                    <v-col cols="3" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{formatMeasureTime(sample.measureDate)}}</p>
                    </v-col>
                    <v-col cols="3" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{sample.flow !== undefined ? formatNumber(sample.flow, 2) : '-'}}</p>
                    </v-col>
                    <v-col cols="1" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{sample.q !== undefined ? sample.q : '-'}}</p>
                    </v-col>
                    <v-col cols="1" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{sample.s !== undefined ? sample.s : '-'}}</p>
                    </v-col>
                    <v-col cols="1" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{sample.vel !== undefined ? formatNumber(sample.vel, 3) : '-'}}</p>
                    </v-col>
                    <v-col cols="3" class="px-1 py-0">
                      <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{sample.eqPressure !== undefined ? formatNumber(sample.eqPressure, 2) : '-'}}</p>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </template>
          </template>
          <template v-if="selectedOption === 'Estado al momento de la medición'">
            <v-row class="mt-3">
              <v-col>
                <!-- Device State -->
                <v-row>
                  <p class="mb-0 ml-2" style="font-size: 1.13rem; font-weight: 600;">Estado del equipo</p>
                  <v-icon v-if="flowMeasurementData.device && flowMeasurementData.device.locationLatitude && flowMeasurementData.device.locationLongitude" @click="goToLocation({latitude: flowMeasurementData.device.locationLatitude, longitude: flowMeasurementData.device.locationLongitude})" class="ml-4" size="1.38rem" color="#0099E1">mdi-map-marker</v-icon>
                  <v-icon v-if="getEqStatusImages(flowMeasurementData.eqStatus).length > 0" @click="openImagesDialog(getEqStatusImages(flowMeasurementData.eqStatus))" class="ml-4" size="1.38rem" color="#0099E1">mdi-camera-outline</v-icon>
                </v-row>
                <v-row>
                  <v-col cols="3" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Movimiento del equipo</p>
                  </v-col>
                  <v-col cols="2" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Velocidad</p>
                  </v-col>
                  <v-col cols="3" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Presión</p>
                  </v-col>
                  <v-col cols="4" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Comentarios</p>
                  </v-col>
                </v-row>
                <v-row class="mt-1">
                  <v-col cols="3" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{flowMeasurementData.eqStatus && flowMeasurementData.eqStatus.eqStatusValue && flowMeasurementData.eqStatus.eqStatusValue.name || '-'}}</p>
                  </v-col>
                  <v-col cols="2" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{flowMeasurementData.eqStatus && flowMeasurementData.eqStatus.speed ? formatNumber(parseFloat(flowMeasurementData.eqStatus.speed) * 100, 2) : '-'}}%</p>
                  </v-col>
                  <v-col cols="3" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{flowMeasurementData.eqStatus && flowMeasurementData.eqStatus.pressure ? formatNumber(flowMeasurementData.eqStatus.pressure, 2) : '-'}} PSI</p>
                  </v-col>
                  <v-col cols="4" class="px-1 py-0">
                    <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{flowMeasurementData.eqStatus && flowMeasurementData.eqStatus.observations ? flowMeasurementData.eqStatus.observations : '-'}}</p>
                  </v-col>
                </v-row>

                <!-- Pump State -->
                <template v-if="flowMeasurementData.pumps && flowMeasurementData.pumps.length > 0">
                  <v-row>
                    <p class="mb-0 ml-2" style="font-size: 1.13rem; font-weight: 600;">Estado de la bomba</p>
                    <v-icon v-if="somePumpHasCoordinates(flowMeasurementData.pumps)" @click="goToLocation(getPumpCoordinates(flowMeasurementData.pumps))" class="ml-4" size="1.38rem" color="#0099E1">mdi-map-marker</v-icon>
                    <v-icon v-if="getPumpsImages(flowMeasurementData.pumps).length > 0" @click="openImagesDialog(getPumpsImages(flowMeasurementData.pumps))" class="ml-4" size="1.38rem" color="#0099E1">mdi-camera-outline</v-icon>
                  </v-row>
                  <template v-for="(pump, pumpIndex) in flowMeasurementData.pumps">
                    <v-row v-if="pumpIndex === 0">
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Tipo de bomba</p>
                      </v-col>
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 400;">RPM</p>
                      </v-col>
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Presión de pozo</p>
                      </v-col>
                      <v-col v-if="pump.pipe && pump.pipe.diameter" class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Cañería</p>
                      </v-col>
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Distancia a equipo</p>
                      </v-col>
                      <v-col cols="3" class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 400;">Comentarios</p>
                      </v-col>
                    </v-row>
                    <v-row class="mt-1">
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{pump.type && pump.type.name || '-'}}</p>
                      </v-col>
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{pump.pumpStatus && formatNumber(pump.pumpStatus.rpm, 2) || '-'}}</p>
                      </v-col>
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{pump.pumpStatus && pump.pumpStatus.pumpPressure ? formatNumber(pump.pumpStatus.pumpPressure, 2) : '-' }} PSI</p>
                      </v-col>
                      <v-col v-if="pump.pipe && pump.pipe.diameter" class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{pump.pipe.diameter}}</p>
                      </v-col>
                      <v-col class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{pump.pumpStatus && formatNumber(pump.pumpStatus.deviceDistance, 1) || '-'}} m</p>
                      </v-col>
                      <v-col cols="3" class="px-1 py-0">
                        <p class="mb-0" style="font-size: 1rem; font-weight: 300;">{{pump.observations || pump.pumpStatus.description ? `${pump.observations} ${pump.observations ? `- ${pump.pumpStatus.description}` : pump.pumpStatus.description}` : '-'}}</p>
                      </v-col>
                    </v-row>
                  </template>
                </template>
              </v-col>
            </v-row>
          </template>
        </div>
      </v-container>
    </v-row>
  </v-container>
</template>

<script>
import { isoDateToSlashes, isObservationsDocumentEmpty, formatNumberHelper } from '../../lib/helpers'
import { getFormatDateFromISO } from '../../lib/time-transformations'
import KeystoneDocumentRenderer from './KeystoneDocumentRenderer.vue'
import _flatMap from 'lodash/flatMap'
import _get from 'lodash/get'
import _flatten from 'lodash/flatten'
import _map from 'lodash/map'

export default {
  props: {
    flowMeasurementData: {
      type: Object,
      required: true
    }
  },
  components: {
    KeystoneDocumentRenderer
  },
  data() {
    return {
      options: [
        'Recomendaciones',
        'Resultados',
        'Estado al momento de la medición'
      ],
      selectedOption: 'Recomendaciones',
      flowMeasurementMethodDialog: false,
      commentsDialog: false,
      comments: '',
      imagesDialog: false,
      images: [],
      carouselModel: 0
    }
  },
  computed: {
    sortedFlowSamples() {
      if (!this.flowMeasurementData.flowSamples) {
        return []
      }
      return [...this.flowMeasurementData.flowSamples].sort((a, b) => new Date(a.measureDate) - new Date(b.measureDate))
    }
  },
  methods: {
    openCommentsDialog (comments) {
      this.commentsDialog = true
      this.comments = comments
    },
    openImagesDialog (images = []) {
      this.images = images
      this.carouselModel = 0
      this.imagesDialog = true
    },
    parseDate (ISODate) {
      return isoDateToSlashes(ISODate)
    },
    checkObservationsDocumentEmpty () {
      return isObservationsDocumentEmpty(this.flowMeasurementData)
    },
    formatNumber (num, decimals = 1) {
      return formatNumberHelper(num, decimals)
    },
    formatMeasureTime (measureDate) {
      return getFormatDateFromISO(measureDate, "dd'/'LL'/'yyyy' 'HH':'mm")
    },
    goToLocation ({latitude, longitude}) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank')
    },
    somePumpHasCoordinates (pumps) {
      return pumps.some(pump => pump.coordinates)
    },
    getPumpCoordinates (pumps) {
      let coordinates = pumps.find(pump => pump.coordinates).coordinates
      coordinates = coordinates.trim().split(',')
      return {
        latitude: coordinates[0],
        longitude: coordinates[1]
      }
    },
    getPumpsImages (pumps) {
      if (!pumps || !Array.isArray(pumps)) {
        return []
      }
      return _flatten(_flatMap(pumps, pump => {
        const pumpStatus = _get(pump, 'pumpStatus')
        if (!pumpStatus) return []
        const brand = _get(pump, 'brand', '')
        const model = _get(pump, 'model', '')
        const label = `Bomba ${brand} ${model}`.trim()
        const imageArrays = [
          _get(pumpStatus, 'rpmImages', []),
          _get(pumpStatus, 'pressureImages', []),
          _get(pumpStatus, 'motorImages', [])
        ]
        return _flatMap(imageArrays, images => 
          _map(images, image => ({ ...image, label }))
        )
      }))
    },
    getFlowSamplesImages (samples, startIndex = 0) {
      if (!samples || !Array.isArray(samples)) {
        return []
      }
      return _flatten(_flatMap(samples, (sample, index) => {
        const imageArrays = [
          _get(sample, 'flowMeterImages', []),
          _get(sample, 'pressureImages', [])
        ]
        const label = `Medición ${startIndex + index + 1}`
        return _flatMap(imageArrays, images => 
          _map(images, image => ({ ...image, label }))
        )
      }))
    },
    getEqStatusImages (eqStatus) {
      if (!eqStatus) {
        return []
      }
      const imageArrays = [
        _get(eqStatus, 'structureImages', []),
        _get(eqStatus, 'frontPanelImages', []),
        _get(eqStatus, 'pressureImages', [])
      ]
      const label = 'Estado del equipo'
      return _flatten(_map(imageArrays, images => 
        _map(images, image => ({ ...image, label }))
      ))
    }
  }
}
</script>

<style scoped>
.flow-measurement-title {
  font-size: 1.38rem;
  font-weight: 600;
  color: #00202F;
}

.flow-measurement-subtitle {
  font-size: 1rem;
  font-weight: 400;
  color: #0099E1;
}

.Options {
  flex: 0 0 200px !important;

  .v-item--active>.v-label {
    color: #0098E1;
  }
}

.Graphic {
  position: relative;
  min-width: 50%;
  flex: 1;
}

.flow-measurement-text-body {
  color: #00202F;
  font-size: 1rem;
  font-weight: 300;
}

</style>


// WEBPACK FOOTER //
// FlowMeasurement.vue?4428875a